import { newNC } from '@/apis/paiban'
import { languageMap } from '@/data/i18n/json/templateMap'
import { IBigPlank } from '@/partTypes'
import store from '@/store'
import {
  deepCopy,
  genCCWSideHoleSlot,
  judgeIsMergeFrameDoor,
  splitEdgeInfo,
} from '@/util/LayoutFuncs'
import { changeDataStartPoint } from '@/util/NCGenerator'
import { pickNcPartFields } from '@/util/NcFileRelated'
import { dealHighlightMatCode } from '@/util/commonFun'
import { getOriginRect, getOtherSideMachineTargets } from '@/util/commonFuncs'
import { transformToEzSort } from '@/util/connect-color-label/transform'
import {
  countSameSizeBoard,
  getEdgeTypeWithRect,
  isLongPlateProcessing,
} from '@/util/createTag'
import defaultTagData, { defaultPaibanData } from '@/util/defaultTagData'
import { dealPlankNcName, genHandleSlopesStr } from '@/util/plankCommonFuncs'
import {
  SettingType,
  appendSubTagFileNameSuffix,
  dealFileArrLabel,
  dealFileArrfunc,
  rollOverTag,
} from '@/util/productionDataGenerate/tagDataGenerate'
import {
  getGraghEdge,
  getSurplusSize,
  judgeIsGenerateLaveTag,
} from '@/util/tag'
import { fontFamilyMap } from '@/util/tag/pdf-draw'
import { FontKey, fontToOss } from '@/util/tag/pdfFont'
import { PartType, PlankType } from '@/views/materialsList/types/part'
import { message } from 'ant-design-vue'
import { nanoid } from 'nanoid'

import { TagSettingType } from '../type'

interface TagPayload {
  templateData: any
  paibanData: any[]
  configData: {
    format: string
    type: 'img' | 'pdf'
    byte?: number
    ncSetting?: any
    fileSetting: SettingType
    ncOutputSetting?: any
    bigPlankTemp?: any
    ncNameSuffix?: string
    isThirdAccount?: boolean
    tagSetting?: TagSettingType
    dpiArr?: number[]
    paibanTime?: string
    isFollowExportTag?: boolean
    typesettingBatch?: string
    fileName?: string
    fileNameList?: any[]
    isSendToFactory?: boolean
  }
}

export interface TagType {
  src: string
  orderNo: string
  partuniqueID: string
  stockKey: string
  stockNum: number
  plank: PlankType
  part: PartType
  plank_index: number
  type: string
  tagFileName?: string
  isRestTag?: boolean
}

/** 支持的语言 */
type Lang =
  | 'zh'
  | 'en'
  | 'kz'
  | 'vi'
  | 'th'
  | 'id'
  | 'ug'
  | 'ben'
  | 'kor'
  | 'bur'
  | 'tr'
  | 'km'
  | 'es'
  | 'ar'
interface FontInfo {
  font: FontKey
  url: string
  source: string
}
class TagDraw {
  fontList: FontInfo[] = []
  imgSrcList: TagType[] = []
  qtCallBack?: (res: any) => void
  tagDataList: {
    part: PartType
    plank: IBigPlank
    plank_index: number
    total_part: number
    template: any
    label_num: number
    isPlank?: boolean
    type?: string
    isRestTag?: boolean
  }[] = []
  worker: null | Worker = null
  setFontList(fontinfo: FontInfo) {
    this.fontList.push(fontinfo)
  }

  /**
   *
   * @param fontFamily 字体
   * @param lang 语言
   * @description 先通过字体找到对应的字体资源，然后根据字体支持的语言来判断是否使用该字体，默认为微软雅黑msyh
   */
  async loadFontByLang(
    fontFamily: keyof typeof fontFamilyMap,
    lang: Lang = 'zh'
  ) {
    try {
      /** 默认字体为微软雅黑 */
      const font = (fontFamilyMap[fontFamily] || 'msyh') as FontKey
      /** 检查是否加载过，加载过的字体无需重复加载 */
      const hasLoaded = this.checkFontHasLoaded(font)
      if (hasLoaded) return
      const mapItem = fontToOss.get(font)
      let langTarget = mapItem?.fontList[0]

      if (mapItem?.supportLangs.includes(lang)) {
        langTarget = mapItem.fontList[0]
      } else {
        langTarget = fontToOss.get('msyh')?.fontList[0]
      }

      const result = await (await fetch(langTarget?.url ?? '')).text()
      this.setFontList({
        font,
        url: langTarget?.url ?? '',
        source: result,
      })
    } catch (error) {
      message.error(`【${fontFamily}】字体加载失败，尝试刷新页面重试`)
    }
  }

  /**
   *
   * @param temp 标签模版中的每项数据
   * @description 根据模版数据中，每项数据设置的字体和语言进行字体资源的获取
   */
  async loadFontByTagTemp(
    temp: {
      data: {
        fontFamily: keyof typeof fontFamilyMap
        /** 此数据并不是模板中的，只是为了以后可以正常处理其他类型的文字而添加的，让此代码不局限在模板 */
        lang: Lang
        [key: string]: any
      }
    }[]
  ) {
    // 从模版中拿到所需要的所有字体统一注册
    const fontFamilyList = temp
      .map((item) => item.data?.fontFamily)
      .filter(Boolean)
    const fontFamilySet = new Set(fontFamilyList)
    for (const fontFamily of fontFamilySet) {
      await this.loadFontByLang(fontFamily)
    }
  }

  /** 检查是否已经被加载过
   * @param font 字体
   */
  checkFontHasLoaded(font: FontKey) {
    return this.fontList.some((f) => f.font === font)
  }

  /**
   *
   * @param data 生成标签的参数
   * @param callBack
   */
  async exportTag(
    data: TagPayload,
    callBack?: (
      re:
        | { data: TagType[]; isFinished: boolean; index: number }
        | { src: string }
    ) => void
  ) {
    this.imgSrcList = []
    transformToEzSort.clear()
    const webToQt = window.webToQt

    const paibanData = JSON.parse(
      JSON.stringify(
        data.paibanData.length ? data.paibanData : defaultPaibanData
      )
    ) as any[]
    const templateData = data.templateData

    let surplusTempData =
      templateData.surplus_tem_data ?? templateData.surplus_data
    if (!surplusTempData) {
      // 设置为默认模板
      surplusTempData = JSON.parse(defaultTagData).surplus_tem_data
    }
    const tempDataList = [
      ...surplusTempData,
      ...templateData.tem_data,
      ...(templateData.sub_tem_data ?? []),
    ]
    const isSpecialLang = ['ug', 'th'].includes(store.state.exportLanguage)
    await this.loadFontByTagTemp(tempDataList)
    if (isSpecialLang) {
      const speLangMap = {
        ug: '维语',
        th: '泰语',
      }
      await this.loadFontByLang(
        speLangMap[
          store.state.exportLanguage as keyof typeof speLangMap
        ] as keyof typeof fontFamilyMap,
        store.state.exportLanguage
      )
    } else {
      await this.loadFontByLang('微软雅黑', store.state.exportLanguage)
    }
    let fontList = this.fontList
    // 如果不是导出的特殊语言，则不传特殊字体
    if (!isSpecialLang) {
      fontList = fontList.filter((i) => !['uighur', 'sarabun'].includes(i.font))
    }
    const sameSizeBoardCount = countSameSizeBoard(
      paibanData
        .map((p) => p.parts)
        .flat(Infinity)
        .map((p) => ({ part: p }))
    )

    const pp = dealDrawDataBeforeExport(
      paibanData,
      data.configData.ncSetting,
      data.configData.fileSetting,
      data.configData.ncOutputSetting
    )

    const translateMap = store.state.templateMap
      ? Object.fromEntries(store.state.templateMap)
      : {}
    const lang = languageMap.get(store.state.exportLanguage ?? 'zh')
    const currentLangTransMap = {
      [lang]: translateMap[lang],
    }

    const isBatchTagging = pp.some((p) => p.isBatchTagging)

    if (
      !data.templateData.surplus_tem_data &&
      !data.templateData.surplus_data
    ) {
      data.templateData.surplus_data =
        JSON.parse(defaultTagData).surplus_tem_data
    }
    // console.log(pp)

    const payload = {
      templateData: JSON.stringify(data.templateData) ?? defaultTagData,
      paibanData: pp,
      configData: {
        ...data.configData,
        uid: store.state.userInfo.id,
        fontList,
        sameSizeBoardCount,
        translateMap: currentLangTransMap ?? {},
        lang: languageMap.get(store.state.exportLanguage ?? 'zh'),
        isBatchTagging,
      },
    }

    if (webToQt) {
      // console.log(payload, 'payload')
      if (this.qtCallBack) {
        webToQt.drawTagFinished.disconnect(this.qtCallBack)
      }

      this.qtCallBack = (res: any) => {
        const resList = res.data
        const type = res.type
        const hasFinished = res.hasFinished
        const plkIndex = res.index
        let realSrcList = resList

        if (type === 'pdf') {
          if (!data.configData.isSendToFactory) {
            webToQt.saveTagPdf(`${data.configData.fileName ?? 'test'}.pdf`)
          }
          callBack && callBack(resList)
        } else {
          for (let idx = 0; idx < resList.length; idx++) {
            const item = resList[idx]
            const { type, partUniqueId, stockKey } = item
            // 兼容自定义文件夹的取值小板直接取
            item.plankId = partUniqueId
            // 如果是大板标签Qt 处理哪里没有返回此参数就需要根据 stockKey 来查找板件得到大板唯一 ID
            if (type === 'plank') {
              const targetBigPlank = data.paibanData.find(
                (v) => v.stockKey === stockKey
              )
              item.plankId = targetBigPlank?.bigPlankUniqueId
            }
          }
          try {
            const tagDataArr = resList
              .filter((tagObj: any) => {
                return tagObj.mark && tagObj.mark == 'tagDataMark'
              })
              .map((tagItem: any) => {
                return {
                  ...tagItem,
                  type: tagItem.isRestTag ? 'lave' : tagItem.type,
                }
              })

            realSrcList = resList.filter((tagObj: any) => {
              return !tagObj.mark
            })
            this.tagDataList.push(...tagDataArr)

            if (hasFinished) {
              this.tagDataList.forEach((i) => {
                const payloadObj = { ...i }
                if (
                  payloadObj.type &&
                  ['part', 'surplus'].includes(payloadObj.type) &&
                  !payloadObj.isRestTag
                ) {
                  if (i.isPlank) {
                    payloadObj.template = data.configData.bigPlankTemp
                  } else {
                    payloadObj.template = {
                      ...templateData,
                      statisticsCount: sameSizeBoardCount,
                    }
                  }
                  if (Reflect.has(payloadObj.part, 'parts')) {
                    payloadObj.part = (payloadObj.part as any).parts[0]
                  }
                  payloadObj.label_num = i.label_num - 1
                  transformToEzSort.setPage(i.label_num)
                  transformToEzSort.generateExtraDatasources([], payloadObj)
                }
              })
            }
            const dataLst = realSrcList.map((tagObj: any) => {
              const base64Str =
                `data:image/${data.configData.format ?? 'png'};base64,` +
                tagObj.src
              return { ...tagObj, src: base64Str }
            })
            this.imgSrcList.push(...dataLst)
            if (data.configData.isFollowExportTag) {
              if (hasFinished) {
                this.worker && this.worker.terminate()
                let payloadObj = {
                  data: dataLst,
                  isFinished: hasFinished,
                  index: plkIndex,
                }
                callBack && callBack(payloadObj)
                payloadObj = null as any
                this.imgSrcList = []
              }
            } else {
              let payloadObj = {
                isFinished: hasFinished,
                data: dataLst,
                index: plkIndex,
              }
              if (hasFinished) {
                this.imgSrcList = []
              }
              callBack && callBack(payloadObj)
              payloadObj = null as any
            }
          } catch (error) {
            console.log(error, 'error')
          }
        }
      }
      webToQt.drawTagFinished.connect(this.qtCallBack)
      webToQt.drawTag(JSON.stringify(payload))
    }
  }
}

export function dealDrawDataBeforeExport(
  paibanData: any[],
  ncSetting: any,
  fileSetting: SettingType,
  ncOutputSetting: any
) {
  const wardrobeNameIndexArr: string[] = []

  const hasProcessSetting = ncSetting.process_setting_id

  // 更改小板的startX 更改孔槽的所在面 也就是左右翻面 应急码 TURNBLANK
  if (
    ncSetting?.emergence_setting_key?.includes('TURNBLANK') &&
    ncSetting.labelOverturn
  ) {
    rollOverTag(paibanData, ncSetting)
  }

  return paibanData.map((plank, index) => {
    const { parts } = plank

    const usedRate = plank.realUsedRate
    const usedArea = plank.realAreaTotal
    // 是否生成余料标签
    const isGenerateLaveTag = judgeIsGenerateLaveTag(usedRate, usedArea)

    const ncName = hasProcessSetting
      ? dealPlankNcName(plank, index, fileSetting, ncOutputSetting)
      : `${index + 1}`

    // 大板绘制信息
    const { size, min_long, min_width } = hasProcessSetting
      ? getGraghEdge(plank)
      : {
          size: `${plank.plankWidth}*${plank.plankHeight}*${plank.thick}`,
          min_long: 0,
          min_width: 0,
        }
    plank.plankSize = size
    plank.sizeObj = {
      height: plank.plankHeight,
      width: plank.plankWidth,
      min_long,
      min_width,
    }
    const normalPart = parts.find((part: any) => !part.specialType)
    const plankDrawMsg = {
      programAddress:
        normalPart && normalPart.address
          ? normalPart.address
          : parts[0].address
          ? parts[0].address
          : parts[0].from_address,
      orderNo:
        normalPart && normalPart.orderNo
          ? normalPart.orderNo
          : parts[0].orderNo
          ? parts[0].orderNo.trim()
          : parts[0].from_order_num,
      material: plank.thick + plank.matCode + plank.texture,
      size,
      ncName,
      qrCode: ncName,
      plank_index: `第${index + 1}张大板`,
      lable_index: ``,
    }

    // 不下料大板
    const isDesMaterialNoCut = parts.some(
      (part: any) => part.isDesMaterialNoCut
    )

    const { xyReverse } = ncSetting

    const fromAddress = [
      ...new Set(
        parts.filter((p: any) => p.address).map((p: any) => p.address)
      ),
    ].join('/')
    const hasCutPart = parts.some((p: any) => p.surplusPath)
    const fromOrderNumArr = hasCutPart
      ? parts
          .filter((p: any) => p.from_order_num)
          .map((p: any) => p.from_order_num)
      : parts.filter((p: any) => p.orderNo).map((p: any) => p.orderNo)
    const fromOrderNum = [...new Set(fromOrderNumArr)].join('/')

    const newParts = parts.map((part: any) => {
      // 将宽高恢复成默认顺序
      const oSizeRect = getOriginRect(part)
      const oSizeCutRect = getOriginRect(part, 'realRect')
      if (part.srcTexDir == 'reverse') {
        const copyOSize = JSON.parse(JSON.stringify(oSizeRect))
        const copyOSizeCut = JSON.parse(JSON.stringify(oSizeCutRect))
        oSizeRect.width = copyOSize.height
        oSizeRect.height = copyOSize.width
        oSizeCutRect.width = copyOSizeCut.height
        oSizeCutRect.height = copyOSizeCut.width
      }
      // 定义特殊字段
      const surplusSizeObj =
        part.surplusPath && part.surplusPath.length
          ? getSurplusSize(part, ncSetting)
          : undefined

      // 归方信息处理
      const isGuifang = hasProcessSetting
        ? isLongPlateProcessing(part, ncSetting)
        : false

      const loc = part.loc
      const roomID = part.roomID
      if (loc && roomID && !wardrobeNameIndexArr.includes(`${loc}_${roomID}`)) {
        wardrobeNameIndexArr.push(`${loc}_${roomID}`)
      }

      // 封边预铣值
      // 构造新封边的展示 1(0.1) ←A↓B→C↑D
      const { left, right, top, bottom } = splitEdgeInfo(part.edgeInfo)
      const preEdgeWidth = part.edgePreMillingInfo
        ? part.edgePreMillingInfo?.width
        : 0
      let newEdgeInfo = part.edgeInfo
      if (preEdgeWidth) {
        newEdgeInfo = `←${left}(${preEdgeWidth})↓${bottom}(${preEdgeWidth})→${right}(${preEdgeWidth})↑${top}(${preEdgeWidth})`
      }

      const targets = getOtherSideMachineTargets(part, false)
      const targetF = getOtherSideMachineTargets(part, true)
      /** 侧孔机加工顺序正面 */
      const sideHoleMachineProcessing = genCCWSideHoleSlot(targets, xyReverse)
      /** 侧孔机加工顺序反面 */
      const sideHoleMachineProcessingF = genCCWSideHoleSlot(targetF, xyReverse)
      const holes = part.holes ?? []
      const slots = part.slots ?? []
      const holesAndSlots = [...holes, ...slots]
      const hasAfterMachineProcessing =
        holesAndSlots.length &&
        !(
          holesAndSlots.every((item: any) => item.process_in_engraving) ||
          holesAndSlots.every((item: any) => item.process_in_aio)
        )
      return {
        ...part,
        isGuifang,
        newEdgeInfo,
        sideHoleMachineProcessing,
        sideHoleMachineProcessingF,
        wardrobeNum: wardrobeNameIndexArr.indexOf(`${loc}_${roomID}`) + 1,
        surplusSizeInfo: surplusSizeObj,
        edgeInfoLength: getEdgeTypeWithRect(part.edgeInfo, part.rect),
        handleSlopesStr: genHandleSlopesStr(part),
        ncName,
        surplusSizeObj,
        // 成品尺寸
        oSize: `${(+oSizeRect.height).toFixed(2)}*${(+oSizeRect.width).toFixed(
          2
        )}*${part.thick}`,
        // 切割尺寸
        cutSize: `${oSizeCutRect.height.toFixed(
          2
        )}*${oSizeCutRect.width.toFixed(2)}*${part.thick}`,
        // 材质
        matCode_texture: `${dealHighlightMatCode(part.matCode)}-${
          part.texture
        }`,
        plankSize: size,
        from_address: fromAddress,
        from_order_num: fromOrderNum,
        address: part._isBujian
          ? part._bujianAddress ?? part.address
          : part.address ?? '',
        orderNo: part._isBujian
          ? part._bujianOrderNo ?? part.orderNo
          : part.orderNo ?? '',
        customerName: part._isBujian
          ? part._bujianCustomerName ?? part.customerName
          : part.customerName ?? '',
        customer: part._isBujian
          ? part._bujianCustomerName ?? part.customer
          : part.customer ?? '',
        plankNum: part._isBujian
          ? part._bujianPlankNum ?? part.plankNum
          : part.plankNum ?? '',
        hasAfterMachineProcessing,
      }
    })
    // 获取骨骼门基板（标签只生成基板标签，不生成小条子及合并板件标签）
    function getFrameDoorBasePart(part: any) {
      if (!judgeIsMergeFrameDoor(part)) {
        return part
      }
      const basePart = part.frame_door_children.find(
        (child: any) => child && child.plankType === 'SingleDoor'
      )
      const resPart = window.$copy(part)
      const defaultPart = basePart || part.frame_door_children[0] || part
      resPart.frameRect = part.rect
      Object.assign(resPart, defaultPart)
      resPart.partUniqueId = nanoid()
      resPart.startX = part.startX
      resPart.startY = part.startY

      return resPart
    }
    /** 骨骼门基板标签数据 */
    const baseParts = newParts
      .filter((p: any) => p.frame_door)
      .map((p: any) => getFrameDoorBasePart(p))
      .map((p: any) => ({ ...p, slots: [] }))
    const isBatchTagging = parts.some((p: any) => p.isBatchTagging)
    return {
      ...plank,
      parts: [...newParts.filter((p: any) => !p.frame_door), ...baseParts],
      plankDrawMsg,
      isBatchTagging,
      isDesMaterialNoCut,
      isGenerateLaveTag,
    }
  })
}

export const createElement = (
  tag: string,
  options: Partial<CSSStyleDeclaration>,
  ortherEleSet?: Partial<HTMLElement>
) => {
  const ele = document.createElement(tag)
  for (const key in options) {
    const value = options[key]
    if (value) ele.style[key] = value
  }
  if (ortherEleSet) {
    for (const key in ortherEleSet) {
      const k = key as keyof typeof ortherEleSet
      const value = ortherEleSet[k]
      const check = Object.getOwnPropertyDescriptor(ortherEleSet, k)
      if (check && check.writable) {
        // @ts-ignore
        ele[k] = value
      }
    }
  }
  return ele
}

export async function preparePrintData(
  tagList: TagType[],
  ncSetting: any,
  tagTempData: any,
  currentTagTemp: any,
  paibanData: any[],
  tag_type = 'base64',
  needTranslate = true
) {
  const print_data = [],
    print_data_qt = []
  const separator = ncSetting.label_image_name_separator
  let url = ''
  let ncFileList = []
  for (let index = 0; index < tagList.length; index++) {
    const tagItem = tagList[index]
    const ncCustomLabel = ncSetting.label_image_name_type ?? []
    let ncFileName = ''
    let labelFileName = ''
    let ncAutoIndex = ''
    if (
      (ncCustomLabel.some((type: string) =>
        ['labeling_filename', 'nc_filename', 'auto_index'].includes(type)
      ) ||
        ncSetting.label_img_big_plank_folder) &&
      !ncFileList.length
    ) {
      // 对小板类的切割顺序按照升序排列
      const paibanDataCopy = deepCopy(paibanData) as PlankType[]
      generateLabelNo(paibanDataCopy, ncSetting)
      paibanDataCopy.forEach((item) => {
        item.parts.sort((a, b) => Number(a.priority) - Number(b.priority))
      })

      const newData = changeDataStartPoint(paibanDataCopy)
      newData.forEach((item: any) => {
        item.parts = item.parts.map((part: any) => pickNcPartFields(part))
      })

      const nc = await newNC({
        uid: store.state.userInfo.id,
        data: JSON.stringify(newData),
        id:
          ncSetting.process_setting_name == '默认生产线'
            ? -1
            : ncSetting.process_setting_id,
        keepKnifeOrigin: true,
        from: 'web',
        embedded: sessionStorage.getItem('embedded') ? 1 : 0,
        guigui_version: sessionStorage.getItem('guigui_version'),
        version: 'v1',
        longPlankCuttingSetting: ncSetting.longPlankCuttingSetting,
        from_label: true,
      })

      if (nc?.data?.knife_error || nc.status === 0) {
        // this.showLoading = false
        // this.isPrintFlag = false
        // this.export_loading = false
        // this.$confirm(`${nc.msg}`, '警告', {
        //   confirmButtonText: '确定',
        //   cancelButtonText: '取消',
        //   type: 'error',
        // })
        throw '找不到刀报错'
      }
      ncFileList = nc.data
    }
    ncFileName = dealFileArrfunc(tagItem.stockKey, ncFileList, 'nc')
    labelFileName = dealFileArrfunc(tagItem.stockKey, ncFileList, 'label')
    ncAutoIndex = dealFileArrLabel(tagItem.plank, ncFileList)
    const part = (tagItem.part ? tagItem.part : tagItem.plank.parts[0]) as any
    const fileNameMap = new Map([
      ['address', part['address']],
      ['orderNo', part.orderNo],
      ['nc_filename', ncFileName],
      ['labeling_filename', labelFileName],
      [
        'plankNum',
        ncSetting.genSimpleLabelBarcode
          ? part.simplePlankNum
          : part['plankNum'],
      ],
      ['auto_index', ncAutoIndex],
      ['stockNum', tagItem['stockNum'] ?? tagItem.plank_index],
      ['priority', part['priority']],
      ['labelId', part['labelId']],
    ])
    // debugger
    let fileName = ''
    if (tagItem.type === 'plank') {
      fileName = `${tagItem.plank_index}`
    } else {
      fileName = ncCustomLabel.length
        ? ncCustomLabel
            .map((type: string) => fileNameMap.get(type))
            .join(separator)
        : `${part['lableImgName']}${separator}${tagItem.plank_index}${separator}${tagItem.part['priority']}`
    }
    fileName = appendSubTagFileNameSuffix(fileName, tagItem)
    // fileName = `${parts[p_index].lableImgName}${separator}${
    //     index + 1
    //   }${separator}${parts[p_index]['priority']}`
    url = tagList[index].src
    print_data.push({ name: fileName })
    if (tag_type !== 'svg') {
      print_data_qt.push({
        name: fileName,
        url: needTranslate
          ? url.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
          : '',
      })
    }
  }
  // this.ncFileList = ncFileList
  // this.showLoading = false
  // this.export_loading = false
  // this.export_loading_text = '导出图片'

  const template_name = currentTagTemp.name

  return { print_data, print_data_qt, template_name, ncFileList }
}

export function generateLabelNo(data: PlankType[], ncSetting: any) {
  const start = ncSetting?.front_labeling_start_num || '0'
  const _no = Number(start) - 1
  if (data && data.length) {
    data.forEach((item) => {
      item.parts.forEach((it) => {
        Object.assign(it, {
          auto_index: generateId(start, _no + Number(it.priority)),
        })
      })
    })
  }
}

/**
 * @description: 生成标签编号
 * @param {string} initStart 初始编号
 * @param {string} id 当前编号
 * @return {string} 生成的编号
 */
export function generateId(initStart: string, id: string | number) {
  let _id = id.toString()
  if (_id.length < initStart.length) {
    _id = _id.padStart(initStart.length, '0')
  }
  return _id
}

export const tagDraw = new TagDraw()

tagDraw.loadFontByLang('微软雅黑', 'zh')
